import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Typography, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useRef, useState } from "react";
import { PinnacleTextField } from "../../components/PinnacleTextField";
import getTheme from "../../theme/theme";
import { PinnacleThemes } from "../../theme/types";
import { register } from "../../api/services/auth";
import { handleApiError } from "../../utils/errorHandler";
import { EMAIL_REGEX } from "../../utils/emailValidate";
import { BTN_ACCEPT_AND_CONTINUE, BTN_BACK_TO_LOGIN, BTN_CREATE, REGISTRATION } from "../../utils/constants";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { blue, grey } from "@mui/material/colors";
import PinnacleFormContainer from "../../components/PinnacleFormContainer/PinnacleFormContainer";
import { PinnacleDialog } from "../../components/PinnacleDialog";
import { PrivacyContent } from "../../layouts/TermsAndPrivacy/PrivacyContent";
import { TermsContent } from "../../layouts/TermsAndPrivacy/TermsContent";

export default function RegistrationPage() {
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:600px)');

    const [email, setEmail] = useState('');
    const [formDisabled, setFormDisabled] = useState(false);
    const [emailTouched, setEmailTouched] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [firstNameTouched, setFirstNameTouched] = useState(false);
    const [lastNameTouched, setLastNameTouched] = useState(false);


    const isEmailValid = EMAIL_REGEX.test(email);
    const isFirstNameValid = firstName.trim().length > 0;
    const isLastNameValid = lastName.trim().length > 0;

    const [isAcceptedTerms, setIsAcceptedTerms] = useState<boolean>(false);
    const [readTermsAndServicesOpen, setReadTermsAndServicesOpen] = useState<boolean>(false);
    const [acceptAndContinueEnabled, setAcceptAndContinueEnabled] = useState(false);
    const contentRef = useRef<HTMLDivElement | null>(null);

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        if (!emailTouched) setEmailTouched(true);
    };

    const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFirstName(e.target.value);
        if (!firstNameTouched) setFirstNameTouched(true);
    };

    const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLastName(e.target.value);
        if (!lastNameTouched) setLastNameTouched(true);
    };

    const handleAcceptTerms = () => {
        setReadTermsAndServicesOpen(true);
        setAcceptAndContinueEnabled(false);
    }

    const handleScroll = () => {
        if (!contentRef.current) {
            return;
        };
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;

        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
        setAcceptAndContinueEnabled(isAtBottom);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setEmailTouched(true);
        setFirstNameTouched(true);
        setLastNameTouched(true);

        if (!isAcceptedTerms) {
            pushNotification({
                details: "Please accept the Terms and Services first.",
                type: NotificationTypes.Error,
            });
            return;
        }

        if (!isEmailValid || !isFirstNameValid || !isLastNameValid) {
            return;
        }

        setFormDisabled(true);
        try {
            const registerResponse = await register({ email, firstName, lastName });
            pushNotification({
                details: registerResponse.message,
                type: NotificationTypes.Success,
            });
            navigate("/login");
        } catch (error) {
            const { errorMessage } = handleApiError(error);
            pushNotification({
                details: errorMessage,
                type: NotificationTypes.Error,
            });
        } finally {
            setFormDisabled(false);
        }
    };

    return (
        <>
            <PinnacleFormContainer formName={REGISTRATION}>
                <form onSubmit={handleSubmit} noValidate>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                        {/* FIRST NAME */}
                        <PinnacleTextField
                            error={firstNameTouched && !isFirstNameValid}
                            color={firstNameTouched && !isFirstNameValid ? 'error' : 'primary'}
                            helperText={firstNameTouched && !isFirstNameValid ? 'First name is required' : ''}
                            id="firstName"
                            label="First name"
                            variant="outlined"
                            autoFocus
                            onChange={handleFirstNameChange}
                            onBlur={() => setFirstNameTouched(true)}
                            value={firstName}
                            autoComplete="first-name"
                            required
                            disabled={formDisabled}
                        />

                        {/* LAST NAME */}
                        <PinnacleTextField
                            error={lastNameTouched && !isLastNameValid}
                            color={lastNameTouched && !isLastNameValid ? 'error' : 'primary'}
                            helperText={lastNameTouched && !isLastNameValid ? 'Last name is required' : ''}
                            id="lastName"
                            label="Last name"
                            variant="outlined"
                            onChange={handleLastNameChange}
                            onBlur={() => setLastNameTouched(true)}
                            value={lastName}
                            autoComplete="last-name"
                            required
                            disabled={formDisabled}
                        />

                        {/* EMAIL */}
                        <PinnacleTextField
                            error={emailTouched && !isEmailValid}
                            color={emailTouched && !isEmailValid ? 'error' : 'primary'}
                            helperText={emailTouched && !isEmailValid ? 'Please enter a valid email' : ''}
                            id="email"
                            label="Email Address"
                            variant="outlined"
                            onChange={handleEmailChange}
                            onBlur={() => setEmailTouched(true)}
                            value={email}
                            autoComplete="email"
                            required
                            disabled={formDisabled}
                        />
                    </Box>


                    {/* TERMS AND CONDITIONS */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isAcceptedTerms}
                                        onChange={handleAcceptTerms}
                                        color="primary"
                                        size="small"
                                        sx={{
                                            color: theme.palette.common.white,
                                            '&.Mui-checked': {
                                                color: blue[300],
                                            },
                                        }}
                                    />
                                }
                                required
                                label='Terms and Services'
                                sx={{ color: theme.palette.common.white }}
                            />
                        </Box>
                    </Box>


                    <Box sx={{ mt: 2 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            type="submit"
                            disabled={!isEmailValid || formDisabled}
                            sx={{
                                mb: 1.5,
                                '&.Mui-disabled': {
                                    backgroundColor: grey[800],
                                    color: grey[400],
                                    opacity: 1,
                                },
                            }}
                        >
                            {formDisabled ? (
                                <CircularProgress size={24} sx={{ color: blue[300] }} />
                            ) : (
                                BTN_CREATE
                            )}
                        </Button>
                        <Button
                            fullWidth
                            variant="text"
                            sx={{
                                borderColor: theme.palette.common.white,
                                color: theme.palette.common.white,
                            }}
                            onClick={() => navigate("/login")}
                        >
                            {BTN_BACK_TO_LOGIN}
                        </Button>
                    </Box>
                </form>
            </PinnacleFormContainer>

            <PinnacleDialog
                isOpen={readTermsAndServicesOpen}
                setIsOpen={setReadTermsAndServicesOpen}
                disableBackDropClick
                headerTitle="Terms and Services"
                renderTermsAndServices={true}
                acceptAndContinueEnabled={acceptAndContinueEnabled}
                onAcceptTerms={() => {
                    setIsAcceptedTerms(true);
                    setReadTermsAndServicesOpen(false);
                }}
            >
                <Box
                    ref={contentRef}
                    onScroll={handleScroll}
                    sx={{
                        maxHeight: '60vh',
                        overflowY: 'auto',
                        padding: '20px',

                    }}
                >
                    {/* Terms content here */}
                    <Typography component="p" sx={{ fontSize: '14px !important' }}>
                        <PrivacyContent />
                        <TermsContent />
                    </Typography>
                </Box>

                <Box sx={{ p: 3 }}>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setIsAcceptedTerms(true);
                            setReadTermsAndServicesOpen(false);
                        }}
                        disabled={!acceptAndContinueEnabled}
                        sx={{
                            backgroundColor: acceptAndContinueEnabled ? "rgba(var(--rgb-secondary), 0.9)" : "#333",
                            width: isMobile ? '100%' : '300px',
                            "&.Mui-disabled": {
                                backgroundColor: "#333",
                                color: "#888",
                            },
                        }}
                    >
                        {BTN_ACCEPT_AND_CONTINUE}
                    </Button>
                </Box>
            </PinnacleDialog>
        </>
    );
}